import type { DvaInstance } from 'dva';
import type { History } from 'history';
import React, { Suspense } from 'react';
import { Switch, Route, Redirect, routerRedux } from 'dva/router';

import { ENABLE_MFE, APIHOST } from '@/config';
import { routePath } from '@/helpers/route';
import { FullHeightWrapperWithLoader } from '@/components/common/full-height-wrapper-with-loader';
import { Loader } from '@/components/Loader';
import MultitabDetector from '@/components/app/multitab-detector';
import CatchAllErrorBoundary from '@/components/error-boundary/catch-all-error-boundary';
import AppThemeProvider from '@/app-theme-provider';

import { TimelineWidgetWrapper } from './routes/timeline-widget-wrapper';
import { LazyAdminDashboard } from './routes/admin-dashboard';

const { ConnectedRouter } = routerRedux;

const Dashboard = React.lazy(() => import(/* webpackChunkName: "routes--dashboard" */ './routes/dashboard'));
const SADashboard = React.lazy(() => import(/* webpackChunkName: "routes--sa-dashboard" */ './routes/sa-dashboard'));
const Account = React.lazy(() => import(/* webpackChunkName: "routes--account" */ './routes/account'));
const Company = React.lazy(() => import(/* webpackChunkName: "routes--company" */ './routes/company'));
const Referral = React.lazy(() => import(/* webpackChunkName: "routes--referral" */ './routes/company/Referral'));
const Applicants = React.lazy(() => import(/* webpackChunkName: "routes--applicants" */ './routes/applicants'));
const LoginPage = React.lazy(() => import(/* webpackChunkName: "routes--login" */ './routes/login'));
const SetupPage = React.lazy(() => import(/* webpackChunkName: "routes--setup" */ './routes/setup'));
const PoddingSetup = React.lazy(() => import(/* webpackChunkName: "routes--podding-setup" */ './routes/podding-setup'));
const SetupPassword = React.lazy(
  () => import(/* webpackChunkName: "routes--setup--password" */ './routes/setup/password'),
);
const Positions = React.lazy(() => import(/* webpackChunkName: "routes--Positions" */ './routes/Positions'));

const JobPostingTemplateDetail = React.lazy(async () => {
  const { PositionTemplateDetail } = await import(
    /* webpackChunkName: "routes--position-template-detail" */ './routes/decoupled-job-posting/position/position-template-detail'
  );
  return { default: PositionTemplateDetail };
});

const JobPostings = React.lazy(async () => {
  const { JobPostingIndexPage } = await import(
    /* webpackChunkName: "routes--job-posting-index-page" */ './routes/decoupled-job-posting/job-posting/job-posting-index-page'
  );
  return { default: JobPostingIndexPage };
});

const JobPostingDetail = React.lazy(async () => {
  const { JobPostingDetailPage } = await import(
    /* webpackChunkName: "routes--job-posting-detail" */ './routes/decoupled-job-posting/job-posting/job-posting-detail-page'
  );
  return { default: JobPostingDetailPage };
});

const CreateHiringProcessTemplate = React.lazy(async () => {
  const { CreateHiringProcessTemplate: CreateHiringProcessTemplatePage } = await import(
    /* webpackChunkName: "routes--create-hiring-process-template" */ './routes/decoupled-job-posting/hiring-process-template/create-hiring-process-template'
  );
  return { default: CreateHiringProcessTemplatePage };
});

const EditHiringProcessTemplate = React.lazy(async () => {
  const { EditHiringProcessTemplate: EditHiringProcessTemplatePage } = await import(
    /* webpackChunkName: "routes--edit-hiring-process-template" */ './routes/decoupled-job-posting/hiring-process-template/edit-hiring-process-template'
  );
  return { default: EditHiringProcessTemplatePage };
});

const BulkEdit = React.lazy(
  () => import(/* webpackChunkName: "routes--Positions--bulk-edit" */ './routes/Positions/bulk-edit'),
);
const Schedule = React.lazy(
  () => import(/* webpackChunkName: "routes--schedule--Entry" */ './routes/improved-schedule'),
);

const SignupPage = React.lazy(() => import(/* webpackChunkName: "routes--SignupPage" */ './routes/SignupPage'));
const AllApplicants = React.lazy(
  () => import(/* webpackChunkName: "routes--applicants--AllApplicants" */ './routes/applicants/AllApplicants'),
);
const ErrorPage = React.lazy(() => import(/* webpackChunkName: "routes--ErrorPage" */ './routes/ErrorPage'));
const RegistCheckr = React.lazy(
  () => import(/* webpackChunkName: "routes--integration--RegistCheckr" */ './routes/integration/RegistCheckr'),
);
const AddNewPosition = React.lazy(
  () => import(/* webpackChunkName: "routes--Positions--add" */ './routes/Positions/add'),
);
const EditPosition = React.lazy(
  () => import(/* webpackChunkName: "routes--Positions--edit" */ './routes/Positions/edit'),
);
const Messages = React.lazy(() => import(/* webpackChunkName: "routes--messages" */ './routes/messages'));
const DemoPage = React.lazy(
  () => import(/* webpackChunkName: "routes--MergeTags--Demo" */ './components/MergeTags/Demo'),
);
const ForgotPasswordPage = React.lazy(
  () => import(/* webpackChunkName: "routes--forgot-password" */ './routes/forgot-password'),
);
const ResetPasswordPage = React.lazy(async () => {
  const { ResetPassword } = await import(/* webpackChunkName: "routes--reset-password" */ './routes/reset-password');
  return { default: ResetPassword };
});
const UserOnBoarding = React.lazy(
  () => import(/* webpackChunkName: "routes--user-onboarding" */ './routes/user-onboarding'),
);
const EmployeeOnboarding = React.lazy(
  () => import(/* webpackChunkName: "routes--employee-onboarding" */ './routes/employee-onboarding'),
);
const ADP = React.lazy(() => import(/* webpackChunkName: "routes--sso--adp" */ './routes/sso/adp'));
const ADPRedirect = React.lazy(
  () => import(/* webpackChunkName: "routes--sso--adp--redirect" */ './routes/sso/adp/redirect'),
);
const JobTargetCallback = React.lazy(
  () => import(/* webpackChunkName: "routes--job-target--Callback" */ './routes/job-target/Callback'),
);
const RedirectPage = React.lazy(() => import(/* webpackChunkName: "routes--redirect" */ './routes/redirect'));
const SignupWizard = React.lazy(() => import(/* webpackChunkName: "routes--signup-wizard" */ './routes/signup-wizard'));
const SelectCompany = React.lazy(
  () => import(/* webpackChunkName: "routes--select-company" */ './routes/select-company'),
);

const SourcingToolsPage = React.lazy(() => import('./routes/sourcing-tools'));
const Support = React.lazy(() => import('./routes/support'));
const PayrollPaywall = React.lazy(() => import(/* webpackChunkName: "routes--ErrorPage" */ './routes/payroll-paywall'));

function RouterConfig({ app, history }: { app: DvaInstance; history: History }) {
  const App = React.lazy(async () => {
    const { registerAllModels } = await import(/* webpackChunkName: "models" */ '@/models');
    registerAllModels(app);
    return import(/* webpackChunkName: "routes--App" */ './routes/App');
  });

  const Analytics = React.lazy(async () => {
    await Promise.all([
      app._store.dispatch({ type: 'app/fetchUserInfo' }),
      app._store.dispatch({ type: 'permission/fetchAvailableAccountPermission' }),
    ]);
    return import(/* webpackChunkName: "routes--analytics" */ './routes/analytics');
  });

  const Profile = React.lazy(async () => {
    await Promise.all([
      app._store.dispatch({ type: 'app/fetchGrowthbookFeaturesData' }),
      app._store.dispatch({ type: 'app/fetchUserInfo' }),
    ]);

    const { ApplicantsProfileIndexContainer } = await import(
      /* webpackChunkName: "routes--profile" */ './routes/profile'
    );

    return { default: ApplicantsProfileIndexContainer };
  });

  return (
    // WARNING: DO NOT add an component that require or would fetch user info at this place.
    // Any component that requires or would actively fetch a logged-in user should be placed in
    // src/Routes/app.tsx, https://github.com/helloworld1812/workstream-hr/blob/c02b5cd0fe64fd7a5065d41ccdc9b9b882133560/src/routes/App.tsx#L161C1-L161C1
    // as this is where the code ensures that a user exists.
    <AppThemeProvider>
      <CatchAllErrorBoundary>
        <ConnectedRouter history={history}>
          <Suspense fallback={<FullHeightWrapperWithLoader spinning fullScreen={!ENABLE_MFE} />}>
            <App>
              <Suspense fallback={<Loader spinning />}>
                <Switch>
                  <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
                  <Route exact path="/dashboard" component={Dashboard} />
                  <Route exact path="/dashboard/:brand" component={SADashboard} />
                  <Route exact path="/add-position" component={AddNewPosition} />
                  <Route exact path="/create-position" render={() => <Redirect to="/add-position" />} />
                  <Route exact path="/edit-position/:digestkey" component={EditPosition} />
                  <Route exact path="/positions" component={Positions} />
                  <Route
                    exact
                    path="/job-posting-template/:position_template_id"
                    component={JobPostingTemplateDetail}
                  />
                  <Route exact path="/job-postings" component={JobPostings} />
                  <Route exact path="/job-postings/:digestkey" component={JobPostingDetail} />
                  <Route
                    exact
                    path={routePath.hiringProcessTemplate.createHiringProcessTemplate.router()}
                    component={CreateHiringProcessTemplate}
                  />
                  <Route
                    exact
                    path={routePath.hiringProcessTemplate.editHiringProcessTemplate.router()}
                    component={EditHiringProcessTemplate}
                  />
                  <Route exact path="/positions/bulk-update" component={BulkEdit} />
                  <Route exact path="/success-login" component={LoginPage} />
                  <Route exact path="/login" component={LoginPage} />
                  <Route exact path="/setup" component={SetupPage} />
                  <Route exact path="/podding-setup" component={PoddingSetup} />
                  <Route exact path="/forgot-password" component={ForgotPasswordPage} />
                  <Route exact path="/reset-password/:token" component={ResetPasswordPage} />
                  <Route exact path="/signup" component={SignupPage} />
                  <Route exact path="/applicants/:location/:position/:digestkey" component={Applicants} />
                  <Route exact path="/applicants" component={AllApplicants} />
                  <Route exact path="/applicants/all-applicants" component={AllApplicants} />
                  <Route exact path="/applicants/inactive-applicants" component={AllApplicants} />
                  <Route exact path="/messages" component={Messages} />
                  <Route exact path="/applicant/:id" component={Profile} />
                  <Route exact path="/my-profile" component={Account} />
                  <Route exact path="/account" component={Account} />
                  <Route exact path="/account/:id" component={Account} />
                  <Route exact path="/company" component={Company} />
                  <Route exact path="/company/dashboard" component={LazyAdminDashboard} />
                  <Route exact path="/referral" component={Referral} />
                  <Route path="/analytics" component={Analytics} />
                  <Route exact path="/integration/checkr" component={RegistCheckr} />
                  <Route exact path="/schedule" component={Schedule} />
                  <Route exact path="/error-page" component={ErrorPage} />
                  <Route exact path="/set-password/:token" component={SetupPassword} />
                  <Route exact path="/user-onboarding" component={UserOnBoarding} />
                  <Redirect
                    exact
                    from="/onboarding/processes"
                    to={{
                      pathname: '/admin/onboarding/processes',
                      search: history?.location?.search,
                    }}
                  />
                  <Route exact path="/onboarding" component={EmployeeOnboarding} />
                  <Route exact path="/admin/onboarding" component={EmployeeOnboarding} />
                  <Route path="/admin/onboarding/:page" component={EmployeeOnboarding} />
                  <Route path="/onboarding/:page" component={EmployeeOnboarding} />

                  <Redirect
                    from="/employee-onboarding"
                    to={{
                      pathname: '/onboarding',
                      search: history?.location?.search,
                    }}
                  />
                  <Redirect
                    from="/employee-onboarding/:page"
                    to={{
                      pathname: '/onboarding/:page',
                      search: history?.location?.search,
                    }}
                  />
                  <Route exact path="/sso/adp" component={ADP} />
                  <Route exact path="/adp/callback" component={ADP} />
                  <Route exact path="/adp/redirect" component={ADPRedirect} />
                  <Route exact path="/job-target-callback" component={JobTargetCallback} />
                  <Route exact path="/signup-wizard" component={SignupWizard} />
                  <Route exact path="/select-company" component={SelectCompany} />
                  <Route exact path="/sourcing-tools" component={SourcingToolsPage} />
                  <Route exact path="/support" component={Support} />
                  <Route path="/redirect" component={RedirectPage} />

                  {/* TODO: DongWei cleanup for merge tags */}
                  <Route exact path="/merge-tags-demo" component={DemoPage} />

                  <Route exact path="/payroll-paywall" component={PayrollPaywall} />
                  <Route exact path="/404" component={ErrorPage} />
                  <Route component={ErrorPage} />
                </Switch>
                <MultitabDetector />
                {!ENABLE_MFE && <TimelineWidgetWrapper apiHost={APIHOST} />}
              </Suspense>
            </App>
          </Suspense>
        </ConnectedRouter>
      </CatchAllErrorBoundary>
    </AppThemeProvider>
  );
}

export default RouterConfig;
